import { SyncableEntity } from '@study-tracker/domain';
import { IRepository } from '../interfaces/IRepository';
import { StoreName } from './schema';

export class IndexedDBRepository<T extends SyncableEntity> implements IRepository<T> {
  constructor(
    private readonly dbProvider: () => Promise<IDBDatabase>,
    private readonly storeName: StoreName
  ) {}

  private async getStore(mode: IDBTransactionMode): Promise<{ store: IDBObjectStore; tx: IDBTransaction }> {
    const db = await this.dbProvider();
    const tx = db.transaction(this.storeName, mode);
    const store = tx.objectStore(this.storeName);
    return { store, tx };
  }

  async findAll(): Promise<T[]> {
    const { store } = await this.getStore('readonly');
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const results = (req.result as T[]).filter((item) => item.deletedAt === null);
        resolve(results);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async findById(id: string): Promise<T | null> {
    const { store } = await this.getStore('readonly');
    return new Promise((resolve, reject) => {
      const req = store.get(id);
      req.onsuccess = () => {
        const item = req.result as T | undefined;
        if (!item || item.deletedAt !== null) {
          resolve(null);
        } else {
          resolve(item);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  async findByField<K extends keyof T>(field: K, value: T[K]): Promise<T[]> {
    const { store } = await this.getStore('readonly');
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const all = req.result as T[];
        const filtered = all.filter((item) => item.deletedAt === null && item[field] === value);
        resolve(filtered);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async upsert(entity: T): Promise<T> {
    const { store, tx } = await this.getStore('readwrite');
    const updated: T = {
      ...entity,
      updatedAt: new Date().toISOString(),
      version: (entity.version || 0) + 1,
    };
    return new Promise((resolve, reject) => {
      const req = store.put(updated);
      req.onerror = () => reject(req.error);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error(`Write to ${this.storeName} was aborted.`));
      tx.oncomplete = () => resolve(updated);
    });
  }

  async softDelete(id: string): Promise<T | null> {
    const existing = await this.findById(id);
    if (!existing) return null;

    const tombstone: T = {
      ...existing,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: (existing.version || 0) + 1,
    };
    const { store, tx } = await this.getStore('readwrite');
    return new Promise((resolve, reject) => {
      const req = store.put(tombstone);
      req.onerror = () => reject(req.error);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error(`Delete from ${this.storeName} was aborted.`));
      tx.oncomplete = () => resolve(tombstone);
    });
  }

  async hardDelete(id: string): Promise<void> {
    const { store, tx } = await this.getStore('readwrite');
    return new Promise((resolve, reject) => {
      const req = store.delete(id);
      req.onerror = () => reject(req.error);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error(`Delete from ${this.storeName} was aborted.`));
      tx.oncomplete = () => resolve();
    });
  }

  async findAllIncludingDeleted(): Promise<T[]> {
    const { store } = await this.getStore('readonly');
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    });
  }

  async findUpdatedSince(isoTimestamp: string): Promise<T[]> {
    const { store } = await this.getStore('readonly');
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const all = req.result as T[];
        resolve(all.filter((item) => item.updatedAt > isoTimestamp));
      };
      req.onerror = () => reject(req.error);
    });
  }

  async upsertBatch(entities: T[]): Promise<void> {
    if (entities.length === 0) return;
    const { store, tx } = await this.getStore('readwrite');
    const now = new Date().toISOString();

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error(`Batch write to ${this.storeName} was aborted.`));

      for (const entity of entities) {
        const record: T = {
          ...entity,
          updatedAt: now,
          version: (entity.version || 0) + 1,
        };
        store.put(record);
      }
    });
  }
}
